import { useState, useEffect, useCallback, RefObject } from "react";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  setStreamingConnected,
  setStreamingError,
  updateDetectionBoxes,
  setStreamingGuidanceMessage,
  setImageNextStage,
  updateStreamingMetrics,
  initStreamingSession,
  resetStreamingState,
} from "src/store/inspectionCaptureSlice";
import ImageStreamingWebSocketService from "src/services/imageStreamingWebSocketService";
import { getWebSocketUrl, generateClientId } from "src/config/websocketConfig";

interface UseStreamingServiceProps {
  lowResVideoRef: RefObject<HTMLVideoElement>;
  highResVideoRef: RefObject<HTMLVideoElement>;
  enabled: boolean;
  onReadyForAnalyzer?: () => void;
  containerId?: string;
  targetClasses?: string[];
  activeQuestionIds?: string[];
}

interface UseStreamingServiceReturn {
  streamingService: ImageStreamingWebSocketService | null;
  pauseStreaming: () => void;
  resumeStreaming: () => void;
}

/**
 * Manages WebSocket streaming service lifecycle.
 * Single effect: create → start → cleanup, keyed on step identity and readiness.
 */
export const useStreamingService = ({
  lowResVideoRef,
  highResVideoRef,
  enabled,
  onReadyForAnalyzer,
  containerId,
  targetClasses,
  activeQuestionIds,
}: UseStreamingServiceProps): UseStreamingServiceReturn => {
  const dispatch = useAppDispatch();
  const { streamingConfig, screenWidth, screenHeight } = useAppSelector((state) => state.inspectionCapture);
  const { inspectionId } = useAppSelector((state) => state.workflow);
  const [streamingService, setStreamingService] = useState<ImageStreamingWebSocketService | null>(null);

  const questionIdsKey = JSON.stringify(activeQuestionIds);
  const targetClassesKey = JSON.stringify(targetClasses);

  useEffect(() => {
    const lowResVideo = lowResVideoRef.current;
    const highResVideo = highResVideoRef.current;

    if (!enabled || !lowResVideo || !highResVideo || !inspectionId) {
      return;
    }

    let cancelled = false;

    const service = new ImageStreamingWebSocketService(
      getWebSocketUrl(),
      generateClientId(),
      inspectionId,
      activeQuestionIds || [],
      {
        quality: streamingConfig.quality,
        maxWidth: streamingConfig.maxWidth,
        maxHeight: streamingConfig.maxHeight,
        interval: streamingConfig.interval,
        screenWidth,
        screenHeight,
        resolutionScale: streamingConfig.resolutionScale,
        containerId,
        targetClasses,
      }
    );

    setStreamingService(service);

    const handleDetectionResult = (result: any) => {
      if (result.status !== "success") {
        return;
      }

      if (result.bboxes) {
        dispatch(updateDetectionBoxes(result.bboxes));
      }

      if (result.guidance_message) {
        dispatch(setStreamingGuidanceMessage(result.guidance_message))
      };

      if (result.image_next_stage) {
        dispatch(setImageNextStage(result.image_next_stage));
        if (result.image_next_stage === 'HIGH_RES') {
          service.switchToHighRes();
        } else if (result.image_next_stage === 'LOW_RES') {
          service.switchToLowRes();
        } else if (result.image_next_stage === 'READY_FOR_ANALYZER') {
          service.pauseStreaming();
          onReadyForAnalyzer?.();
        }
      }
    };

    service.startStreaming(
      lowResVideo,
      highResVideo,
      handleDetectionResult,
      // Connection status handler
      (connected: boolean) => {
        dispatch(setStreamingConnected(connected));
        dispatch(setStreamingError(connected ? null : "Connection lost"));
      },
      // Metrics handler
      (metrics: { imageSize?: number; latency?: number }) => {
        dispatch(updateStreamingMetrics(metrics));
      }
    ).then(() => {
      if (!cancelled) {
        dispatch(initStreamingSession());
      }
    }).catch(() => {
      if (!cancelled) {
        dispatch(setStreamingError("Failed to connect to streaming service"));
      }
    });

    return () => {
      cancelled = true;
      service.stopStreaming();
      setStreamingService(null);
      dispatch(resetStreamingState())
    };
  }, [enabled, questionIdsKey, targetClassesKey, containerId, inspectionId]);

  const pauseStreaming = useCallback(() => {
    streamingService?.pauseStreaming();
  }, [streamingService]);

  const resumeStreaming = useCallback(() => {
    if (streamingService?.isConnected()) {
      streamingService.resumeStreaming();
    }
  }, [streamingService]);

  return { streamingService, pauseStreaming, resumeStreaming };
};
